/** Rendering and slicing logic for highlighting */

import { ReactNode } from 'react';
import { AstText, TextRunLookup, TextRun } from '../types';
import { Annotation } from '../../../Annotation/types';
import Highlight from '../../../Highlighting/components/Highlight';
import {
  adjustAnnotationPositionForRun,
  computeRunStartInBlock,
  computePriorDescendantNonWhitespaceForRun,
  findIndexFromNonWhitespaceOffset,
  findSameBlockAnchorRun,
  getFirstRunIdForBlock,
  getRunForTextNode,
  sortAnnotationsByStartPosition,
} from './utils';

export interface TextSlice {
  text: string;
  annotation?: Annotation;
  i: number;
}

function adjustAndSortApplicableAnnotations(
  run: TextRun,
  blockAnnotations: Annotation[],
  textRunLookup: TextRunLookup,
  priorDescendantNonWs: number,
  totalNonWhitespaceChars: number,
): Annotation[] {
  const adjustedAnnotations = blockAnnotations.map((ann) =>
    adjustAnnotationPositionForRun(
      ann,
      run,
      textRunLookup,
      priorDescendantNonWs,
      totalNonWhitespaceChars,
    ),
  );

  const applicableAnnotations = adjustedAnnotations.filter((annotation) => {
    const { start, end } = annotation.position;
    return start.offset < totalNonWhitespaceChars && end.offset > 0;
  });

  if (applicableAnnotations.length === 0) {
    return [];
  }

  return sortAnnotationsByStartPosition(applicableAnnotations);
}

function addPlainSlice(
  slices: TextSlice[],
  text: string,
  start: number,
  end: number,
  sliceIndex: number,
): number {
  if (end > start) {
    slices.push({ text: text.slice(start, end), i: sliceIndex++ });
  }
  return sliceIndex;
}

function skipLeadingWhitespaceInRange(
  text: string,
  fromIndex: number,
  untilIndex: number,
): number {
  let adjustedStart = fromIndex;
  while (adjustedStart < untilIndex && /\s/.test(text[adjustedStart])) {
    adjustedStart++;
  }
  return adjustedStart;
}

function computeHighlightEndIndex(
  text: string,
  initialSliceEnd: number,
  shouldExtendBeyondRun: boolean,
): number {
  if (!shouldExtendBeyondRun) {
    return initialSliceEnd;
  }
  let highlightEndIndex = initialSliceEnd;
  while (
    highlightEndIndex < text.length &&
    /\s/.test(text[highlightEndIndex])
  ) {
    highlightEndIndex++;
  }
  return highlightEndIndex;
}

function computeSlicesForRun(
  text: string,
  run: TextRun,
  blockAnnotations: Annotation[],
  textRunLookup: TextRunLookup,
): TextSlice[] {
  if (!text || blockAnnotations.length === 0) {
    return [{ text, i: 0 }];
  }

  const totalNonWhitespaceChars = run.nonWsEnd - run.nonWsStart;
  const priorDescendantNonWs = computePriorDescendantNonWhitespaceForRun(
    run,
    textRunLookup,
  );

  const sortedApplicable = adjustAndSortApplicableAnnotations(
    run,
    blockAnnotations,
    textRunLookup,
    priorDescendantNonWs,
    totalNonWhitespaceChars,
  );

  if (sortedApplicable.length === 0) {
    return [{ text, i: 0 }];
  }

  const slices: TextSlice[] = [];
  let currentIndex = 0;
  let sliceIndex = 0;

  for (const annotation of sortedApplicable) {
    const { start, end } = annotation.position;
    const sliceStart = findIndexFromNonWhitespaceOffset(text, start.offset);
    const sliceEnd = findIndexFromNonWhitespaceOffset(text, end.offset);

    sliceIndex = addPlainSlice(
      slices,
      text,
      currentIndex,
      sliceStart,
      sliceIndex,
    );

    const adjustedStart = skipLeadingWhitespaceInRange(
      text,
      sliceStart,
      sliceEnd,
    );
    sliceIndex = addPlainSlice(
      slices,
      text,
      sliceStart,
      adjustedStart,
      sliceIndex,
    );

    const highlightStart = adjustedStart;
    const extendsBeyondRun = end.offset >= totalNonWhitespaceChars;
    const highlightEndIndex = computeHighlightEndIndex(
      text,
      sliceEnd,
      extendsBeyondRun,
    );
    const highlightedText = text.slice(highlightStart, highlightEndIndex);
    if (highlightedText) {
      slices.push({ annotation, text: highlightedText, i: sliceIndex++ });
    }
    currentIndex = highlightEndIndex;
  }

  if (currentIndex < text.length) {
    slices.push({ text: text.slice(currentIndex), i: sliceIndex++ });
  }
  return slices;
}

export function renderTextNodeWithHighlights(
  textNode: AstText,
  textRunLookup: TextRunLookup,
  blockAnnotations: Annotation[],
): ReactNode {
  const { text } = textNode;
  if (!text.trim()) {
    return text;
  }

  const run = getRunForTextNode(textNode, textRunLookup);
  if (!run) {
    return text;
  }

  const runStartInBlock = computeRunStartInBlock(run, textRunLookup);

  const filteredAnnotations = blockAnnotations.filter((annotation) => {
    const { start, end } = annotation.position;
    if (start.blockId !== run.blockId && end.blockId !== run.blockId) {
      return false;
    }
    if (start.blockId === end.blockId && start.blockId === run.blockId) {
      const anchor = findSameBlockAnchorRun(
        textRunLookup,
        run.blockId,
        start.offset,
      );
      if (!anchor) {
        return false;
      }
      const thisStartsBeforeEnd = runStartInBlock < end.offset;
      const isAnchorOrAfter = run.runId >= anchor.runId;
      return isAnchorOrAfter && thisStartsBeforeEnd;
    }
    if (start.blockId === run.blockId && end.blockId !== run.blockId) {
      const endFirstRunId = getFirstRunIdForBlock(textRunLookup, end.blockId);
      return run.runId < endFirstRunId;
    }
    return true;
  });

  if (filteredAnnotations.length === 0) {
    return text;
  }

  const slices = computeSlicesForRun(
    text,
    run,
    filteredAnnotations,
    textRunLookup,
  );

  return slices.map((slice) =>
    slice.annotation ? (
      <Highlight
        key={`${slice.annotation.id}-${run.runId}-${slice.i}`}
        annotation={slice.annotation}
      >
        {slice.text}
      </Highlight>
    ) : (
      slice.text
    ),
  );
}
